import React, { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { ESRQuestionGroup } from "./ESRQuestionGroup";
import { useDispatch, useSelector } from "react-redux";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ESRQuestionResponse, ESRState } from "../types/data";
import {
    ESRThunkDispatch,
    fetchESRResponses,
    fetchESRSummary,
    submitESR,
    updateQuestionResponse,
    fetchESRPermissions,
    RESET_ESR_DETAILS,
    RESET_ESR_DETAILS_IN_PROGRESS,
    RESET_ESR_DETAILS_COMPLETE,
    commitESRResponses,
    discardESRResponses,
    fetchConcurrentEdits,
	revokeSpecialEditPermissions,
	// getESRReviewSummary,
} from "../actions/form";
import getBackendURL from "../api/api";
import { download, request } from "../request";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
	Alert,
	Box,
	Button,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import {
	ArrowBack,
	AttachFileOutlined,
	ChatOutlined,
	FileDownload,
	GroupAdd,
} from "@mui/icons-material";
import CircleNotificationsOutlinedIcon from "@mui/icons-material/CircleNotificationsOutlined";
import { User } from "../auth";
import Add from "@mui/icons-material/Add";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { DataAction } from "../types/action";
import { SidebarContext } from "./SidebarLayout";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { ConfirmButton } from "./shared/ConfirmButton";
import { Spinner } from "./shared/Spinner";
import {listMessages} from "../actions/util";
import { FloatingMessage } from "./FloatingMessage";
import { GrantEditPermissionDialog } from "./shared/GrantEditPermissionDialog";

const BULK_EDIT_SPECIAL_UNLOCK_QID = "ESR_BULK_EDIT_SPECIAL_UNLOCK";
export const ESRForm: FC = () => {
	type RecipientOption = [string, string, string, string, string[], boolean];
	const [recipientOptions, setRecipientOptions] = useState<RecipientOption[]>(
		[],
	);
	const [recipients, setRecipients] = useState<string[]>([]);
	// const [groupOptions, setGroupOptions] = useState<string[]>([]);
	const [groups, setGroups] = useState<string[]>([]);
	const [allowed_user_groups, setAllowedGroups] = useState<string[]>([]);
	const [selected_user_groups, setSelectedUserGroups] = useState<string[]>([]);

	const { esrId = "", activeLabel = "", activeId = "", groupIdx, questionId} = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch<ESRThunkDispatch>();
	const [user, setUser] = useCurrentUser();
    const [isDownloading, setIsDownloading] = useState(false);

	let isUserAllowedToViewOrCreateMessages = true;
	if(user?.groups){
		// console.log(user?.groups)
		const groups = user?.groups ?? [];
		isUserAllowedToViewOrCreateMessages = !groups.includes("IDNR");
		if (groups.includes("Admin")) {
			isUserAllowedToViewOrCreateMessages = true;
		}
	}

	const questions = useSelector((state: ESRState) => state.questions);
	const responses = useSelector((state: ESRState) => state.responses);
	// The list of questions and their groupings, plus the current responses to the loaded form
	const curGroupIdx = Number(groupIdx) || 0;

	// Commonly referenced properties of the ESR
	const addendums = useSelector((state: ESRState) => state.addendums);
	const esr_submitted = useSelector((state: ESRState) => state.esr_submitted);
	const esr_sponsor_type = useSelector(
		(state: ESRState) => state.esr_sponsor_type,
	);
	// const filtered_threads = useSelector(
	// 	(state: ESRState) => state.filtered_threads,
	// );
	const threads = useSelector((state: ESRState) => state.threads);
	const unreadMessgCount = useMemo(() => {
		if (threads) {
			return Object.values(threads)
				.filter(
					(thread) => (esrId === activeId) ? 
					(thread?.addendum_id === esrId || thread?.esr_id === esrId) && thread?.unread_messages > 0 :
					(thread?.esr_id === esrId || thread?.addendum_label === activeLabel)  && thread?.unread_messages > 0,
				)
				.reduce((a, b) => a + b.unread_messages, 0);
		}
		return 0;
	}, [threads]);

	const handleRequestResponseChange = () => {
		window.open("/errortickets", "_blank", "noopener,noreferrer");
	};

	const last_updated_question_id = useSelector((state: ESRState) => state.last_updated_question_id);

	// ESR RESET DETAILS loading state
	const reset_esr_complete = useSelector((state: ESRState) => state.esr_reset.complete);

	const [manualSet, setManualSet] = useState(false);
	const [showSubmitBtn, setSubmitVisible] = useState(false);
	const [showRequestChangeResponse, setRequestChangeResponse] = useState(false);
	const [enableSubmitBtn, setSubmitEnabled] = useState(false);
	const [pollingNRU, setPollingNRU] = useState(false);
	const [submitLabel, setSubmitLabel] = useState("Submit to BDE");
	const is_user_permitted_to_view_esr = useSelector(
		(state: ESRState) => state.is_user_permitted,
	);
    const saveComplete = useSelector(
  (state: ESRState) => state.update_bulk_responses_progress.complete);

	const hasPendingResponses = useSelector((state: ESRState) =>
		Object.values(state.responses).some((r) => r.pending === true)
	);
	const update_bulk_responses_in_progress = useSelector((state: ESRState) => state.update_bulk_responses_progress.inProgress);
	const handleSaveBulkResponses = () => {
		const askForMoreChanges = holdsSpecialEditGrant;
		// Fetch concurrent edits first to update the inline display, then commit
		dispatch(fetchConcurrentEdits(activeId))
		.then(() => {
			dispatch(commitESRResponses(activeId));
		})
		.then(()=> {
			if(askForMoreChanges) setMoreChangesDialogOpen(true);
		});
	};
	const handleDiscardResponses = ()=>{
		dispatch(discardESRResponses(activeId));
		navigate(`/esr/${esrId}/${activeLabel}/${activeId}/${curGroupIdx + 1}`);
	}

	const scrolledQuestionId = useRef<string | null>(null);
	const is_organization_viewer_only = useSelector((state: ESRState) => state.is_organization_viewer_only);
	useEffect(() => {
		if (!last_updated_question_id) return;
		if (!saveComplete) return;
		useScrollToQuestion(last_updated_question_id, true);
	}, [saveComplete]);

	function useScrollToQuestion(questionId: string, avoidHighlight: boolean = false) {
			if (scrolledQuestionId.current === questionId) return;
			const targetId = questionId.replaceAll(".", "__");
			let attempts = 0;
			const maxAttempts = 30;
			let rafId: number | null = null;
			const tryScroll = () => {
				const el = document.getElementById(targetId) as HTMLElement | null;
				if (el) {
					el.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
					if (!avoidHighlight) {
						el.style.outline = "2px dashed red";
						el.style.background = "gold";
						setTimeout(() => (el.style.background = ""), 2000);
					}
					scrolledQuestionId.current = questionId;
					return;
				}
				if (attempts++ < maxAttempts) {
					rafId = requestAnimationFrame(tryScroll);
				}
			};
			rafId = requestAnimationFrame(tryScroll);
			return () => {
				if (rafId) cancelAnimationFrame(rafId);
			};
	}

	// jump to question
	useEffect(() => {
		if (!questionId) return;
		if (groupIdx) {
			useScrollToQuestion(questionId);
		} else {
			// current path: /esr/:esrId/redirect/:questionId
			const qid = questionId.split(".")[0];
			const groupIdx = questions.groups.findIndex((group) =>
				group.subgroups
					.flatMap((subgroup) => subgroup.questions)
					.map((question) => question.question_id)
					.includes(qid),
			);
			if (groupIdx > -1) {
				if (searchParams !== null)
					navigate(`/esr/${esrId}/${activeLabel}/${activeId}/${groupIdx}/${questionId}?${searchParams}`, {
						replace: true,
					});
				else
				navigate(`/esr/${esrId}/${activeLabel}/${activeId}/${groupIdx}/${questionId}?`, {
					replace: true,
				});
			}
		}
	}, [esrId, activeLabel, activeId, questions, groupIdx, questionId, reset_esr_complete]);

	// useEffect(()=>{
	// 	if (!responses.hasOwnProperty("N25F")){
	// 		setPollingNRU(false);
	// 		return;
	// 	}
	// 	setPollingNRU(true);
	// 	const id = setInterval(() => {
	// 		dispatch(fetchESRResponses(activeId));
	// 	}, 5000);
	// 	return () => clearInterval(id);
	// }, [responses?.['N25F'], activeId, esrId]);

	const setActiveAddendum = (activeLabel: string, activeId: string) => {
			navigate(`/esr/${esrId}/${activeLabel}/${activeId}/${groupIdx}`);
	};

	const determineSubmitStatus = (user: User, responses: Record<string, ESRQuestionResponse>) => {
		// Get info about who the user is
		var isConsultant = false;
		var isTrainedStaff = false;
		var isLRDC = false;
		var isLRD = false;
		var isAeronautics = false;
		var isRailroads = false;
		var isDEC = false;
		user.groups.forEach((g) => {
			if (["Consultant", "Local Public Agency"].includes(g))
				isConsultant = true;
			if (["Trained Staff"].includes(g))
				isTrainedStaff = true;
			if (g.includes("Local Roads District") && /^Local Roads District\s+\d+\s+\S+/.test(g))
				isLRDC = true;
			if (g.startsWith("Local Roads District"))
				isLRD = true;
			if (["Aeronautics"].includes(g))
				isAeronautics = true;
			if (g == "Railroads")
				isRailroads = true;
			if (g.startsWith("District Environmental Cordinator"))
				isDEC = true;
		});

		const nepaProcessing = responses["E25"]?.["response"] ?? null;
		const submittedForTS = (responses["E3T"]?.["response"] ?? '').length > 0;
		const submittedForFunding = (responses["E3F"]?.["response"] ?? '').length > 0;
		const submittedToAeronautics = (responses["E3A"]?.["response"] ?? '').length > 0;
		const submittedToRailroads = (responses["E3R"]?.["response"] ?? '').length > 0;
		const submittedDistrict = (responses["E3"]?.["response"] ?? '').length > 0;
		const submittedToBDE = (responses["E5"]?.["response"] ?? '').length > 0;
		const requestingAgency = responses["E8"]?.["response"] ?? '';
		const isAeroProjectToBeReturned = responses["A1"]?.response == "True";
		const tsScreenSuccess = responses["C32"]?.response == "True" || responses["C35"]?.response == "True";
		const tsEligible = nepaProcessing && (["CE", "Non-Federal", "Reevaluation"].includes(nepaProcessing));
		const tsCertified = responses["E96"]?.response == "True" || responses["E81"]?.response == "True";
		const isTrainedStaffRouting = tsEligible && (tsScreenSuccess && !tsCertified);

		var enableSubmit = true;
		var submitLabel = "Submit to BDE";

		if (requestingAgency == 'Local') {
			if (isConsultant) {
				// it can be 1-step or 2-step submission
				// if 1-step E3FS and E3
				// if 2-step E3T and E3F
				if (isTrainedStaffRouting) {
					if (!submittedForTS) {
						submitLabel = "Submit to District Trained Staff";
					} else {
						submitLabel = "Submitted to District";
						enableSubmit = false;
					}
				} else if (!submittedDistrict) {
					if (!submittedForFunding) {
						submitLabel = "Submit to District for Funding";
					} else {
						submitLabel = "Submitted to District";
						enableSubmit = false;
					}
				}
			} else if (isLRD || isLRDC) {
				if (!submittedToBDE) {
					// it can be 1-step or 2-step submission
					// if 1-step E3FS and E3
					// if 2-step E3T and E3F
					if (submittedDistrict) {
						submitLabel = "Submit to BDE";
					} else if (submittedForTS && !isTrainedStaffRouting && !submittedForFunding) {
						submitLabel = "Submit to District for Funding";
					} else if (submittedForFunding) {
						submitLabel = "Submit to BDE";
					}
				} else {
					submitLabel = "Submitted to BDE";
					enableSubmit = false;
				}
			} else if(isDEC){
				enableSubmit = false;
			}
		}

		if (requestingAgency == 'Railroads') {
			if (isConsultant) {
				if (!submittedToRailroads) {
					submitLabel = "Submit to Railroads";
				} else if (submittedToRailroads && !submittedToBDE) {
					submitLabel = "Submitted to Railroads";
					enableSubmit = false;
				} else if (submittedToBDE) {
					submitLabel = "Submitted to BDE";
					enableSubmit = false;
				}
			}
		}

		if (requestingAgency == 'Aeronautics') {
			if (isConsultant) {
				if (!submittedToAeronautics) {
					submitLabel = "Submit to Aeronautics";
				} else {
					submitLabel = "Submitted to Aeronautics";
					enableSubmit = false;
				}
			}
			else if(isAeroProjectToBeReturned){
				submitLabel = "Project Needs Update";
				enableSubmit = false;
			}
			if(submittedToBDE){
				submitLabel = "Submitted to BDE";
				enableSubmit = false;
			}
		}

		// Check if an N25M lookup is still processing
		// if (responses.hasOwnProperty("N25F")) {
		// 	submitLabel = "Natural L2 Lookup in Progress";
		// 	enableSubmit = false;
		// 	// setPollingNRU(true);
		// 	// schedule polling check
		// 	// pollNaturalCompletion();
		// }

		return {"enable": enableSubmit, "label": submitLabel};
	}

	useEffect(() => {
		setSidebarOpen(false);  // close sidebar on mount
		return () => {
			setSidebarOpen(true);  // open sidebar on unmount
		}
	}, []);

	useEffect(() => {
		if (user) {
			const initialize = async () => {
				dispatch({ type: RESET_ESR_DETAILS_IN_PROGRESS });
				dispatch({ type: RESET_ESR_DETAILS });
				await Promise.all([
					dispatch(fetchESRSummary(activeId)),
					dispatch(fetchESRResponses(activeId)),
					// dispatch(getESRReviewSummary(activeId)),
					dispatch(fetchESRPermissions(activeId)),
					dispatch(listMessages(activeId)),
				]);
				dispatch({ type: RESET_ESR_DETAILS_COMPLETE });
			};
			initialize();
		}
	}, [user, activeId]);

	useEffect(() => {
		if (esr_submitted) {
			setSubmitVisible(false);
			setSubmitEnabled(false);
			setRequestChangeResponse(true);
		}
	}, [esr_submitted]);

	useEffect(() => {
		if (questionId) return;
		if (typeof groupIdx !== "undefined") {
			const idx = Number(groupIdx);
			if (
				questions.groups.length > 0 &&
				idx >= 0 &&
				idx < questions.groups.length
			) {
				if (questions.groups[idx].subgroups.length > 0)
					scrollToSubgroup(questions.groups[idx].subgroups[0].name);
			}
		}
	}, [groupIdx, questionId]);

	useEffect(() => {
		if (activeId !== "" && responses && user) {
			const curr_date = new Date().toISOString().split("T")[0];
			if (esr_submitted) {
				// When certain groups view the ESR, mark the date that review has started
				if (!responses.hasOwnProperty("C33")){
					if (user.groups.includes("CRU Archaeology")) {
						if ((responses["C33"]?.["response"] ?? "") === "") {
							dispatch(updateQuestionResponse(esrId, activeId, "C33", curr_date));
						}
					}
				}
				if(!responses.hasOwnProperty("C63")){
					const requestingAgency = responses["E8"]?.["response"] ?? '';
					const districtNumber = responses["E9"]?.["response"] ?? '';
					if (user.groups.includes("CRU Architecture") || user.groups.includes(`CRU Architecture District ${districtNumber} ${requestingAgency}`)) {
						if ((responses["C63"]?.["response"] ?? "") === "") {
							dispatch(updateQuestionResponse(esrId, activeId, "C63", curr_date));
						}
					}
				}
				if(!responses.hasOwnProperty("R197")){
					let isRSUPhase1 = false;
					user.groups.forEach((g) => {
						if (g.startsWith('RSU Phase I') || g.startsWith('RSU CDB/CI')) isRSUPhase1 = true;
					});
					if (isRSUPhase1) {
						if (
							((("R14" in responses && (responses["R14"]?.["response"] ?? "") === "False") && ((responses["R76"]?.["response"] ?? "") === "")) ||
							(("R76" in responses && (responses["R76"]?.["response"] ?? "") === "False") && ((responses["R92"]?.["response"] ?? "") === "")) ||
							("R92" in responses && (responses["R92"]?.["response"] ?? "") === "False")) &&
							(responses["R197"]?.["response"] ?? "") === ""
						) {
							dispatch(updateQuestionResponse(esrId, activeId, "R197", curr_date));
						}
					}
				}
				if(!responses.hasOwnProperty("N26")){
					if (
						user.groups.includes("NRU") || 
						user.groups.includes("NRU Aeronautics") ||
						user.groups.some(group => group.startsWith("NRU District "))
					) {
						if ((responses["N26"]?.["response"] ?? "") === "") {
							dispatch(updateQuestionResponse(esrId, activeId, "N26", curr_date));
						}
					}
				}
			} else {
				// Is there sufficient information to enable Submit button?
				setSubmitVisible(true);  // TODO: Modify this based on below
				const labelInfo = determineSubmitStatus(user, responses);
				setSubmitEnabled(questions.complete && labelInfo["enable"]);
				setSubmitLabel(labelInfo["label"]);
			}
		}
	}, [esrId, responses, questions, esr_sponsor_type, esr_submitted, user]);

	const submitForm = () => {
		dispatch(submitESR(activeId));
		navigate(`/esr/${esrId}/${activeLabel}/${activeId}`);
	};

	const [searchParams] = useSearchParams();
	useEffect(() => {
		const id = searchParams.get("thread_id");
		if (id !== null) {
			dispatch({
			type: "UPDATE_DRAWER_STATE",
			update: {
				selectedThreadId: Number(id),
			},
		});
			openThreads();
		}
	}, [searchParams]);

	const request_info_from_sponsor: boolean = useSelector((state: ESRState) => state.request_info_from_sponsor);
	const openThreads = () => {
		dispatch<DataAction>({
			type: "UPDATE_DRAWER_STATE",
			update: {
				attachmentsOpen: false,
				threadsOpen: true,
				sentNotificationsOpen: false,
			},
		});
		if (request_info_from_sponsor) {
			dispatch<DataAction>({
				type: "REQUEST_INFO_FROM_SPONSOR_COMPLETE",
			});
		}
		if (activeId) {
					// console.log("message")
					request<{
						users: Record<
							string,
							{
								first_name: string;
								last_name: string;
								organization: string;
								groups: string[];
								is_sponsor: boolean;
							}
						>;
						groups: string[];
						allowed_groups: string[];
					}>(getBackendURL(`/esr/permitted_recipients/${activeId}`)).then(
						({ data }) => {
							let options: RecipientOption[] = Object.entries(data["users"])
								.map(
									([email, u]) =>
										[email, u["first_name"], u["last_name"], u["organization"], u.groups, u.is_sponsor] as [
											string,
											string,
											string,
											string,
											string[],
											boolean,
										],
								)
								.filter(([email]) => email !== user?.email);
							if(request_info_from_sponsor){
								options = options.filter(([, , , , ,is_sponsor]) => is_sponsor);
							}
							setRecipientOptions(options);
							if(request_info_from_sponsor) setAllowedGroups(['Sponsor']);
							else setAllowedGroups([...data["allowed_groups"]]);
						},
					);
					setSelectedUserGroups([]);
					setRecipients([]);
				}
	};

	const openAttachments = () => {
		dispatch({
			type: "UPDATE_DRAWER_STATE",
			update: {
				attachmentsOpen: true,
				threadsOpen: false,
				sentNotificationsOpen: false,
				selectedThreadId: -1,
			},
		});
	};

	const openSentNotifications = () => {
		dispatch({
			type: "UPDATE_DRAWER_STATE",
			update: {
				attachmentsOpen: false,
				threadsOpen: false,
				sentNotificationsOpen: true,
				selectedThreadId: -1,
			},
		});
	};

	const scrollToSubgroup = (name: string) => {
		const selector = `#subgroup-${name.replace(/[^\w]/g, "-")}`;
		document.querySelector(selector)?.scrollIntoView({ block: "start" });
	};

	const { setSidebarOpen } = useContext(SidebarContext);

	// Admin granted edit permissions (individual QIDs or bulk) held by the current user
	const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
	const [moreChangesDialogOpen, setMoreChangesDialogOpen] = useState(false);
	const holdsSpecialEditGrant = Object.entries(responses).some(
		([qid, res]) => qid.endsWith("_SPECIAL_UNLOCK") && !!user?.email && res.response === user.email,
	);
	const bulkEditGrantee = responses[BULK_EDIT_SPECIAL_UNLOCK_QID]?.response ?? "";


	return reset_esr_complete && !update_bulk_responses_in_progress ? (
		<Box sx={{ position: "fixed", left: 0, right: 0, top: 0, bottom: 0 }}>
			{(is_user_permitted_to_view_esr || user?.admin) && (
				<Stack
					direction="row"
					sx={{
						alignItems: "stretch",
						height: "100%",
					}}
				>
					<Stack
						direction="column"
						className="no-print"
						sx={{
							flex: "none",
							width: "336px",
							background:
								"linear-gradient(0deg, rgba(0, 62, 98, 0.02), rgba(0, 62, 98, 0.02))",
							border: "1px solid #0000001F",
							overflow: "hidden",
							opacity: hasPendingResponses ? 0.5 : 1,
							pointerEvents: hasPendingResponses ? "none" : "auto",
						}}
					>
						<Box
							sx={{
								border: "1px solid #0000001F",
								p: "20px 16px",
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
								justifyContent: "space-between",
							}}
						>
							<Button
								variant="text"
								sx={{ maxWidth: 150, textTransform: "capitalize" }}
								onClick={() =>
									navigate(`/esr/${esrId}/${activeLabel}/${activeId}`)
								}
							>
								<ArrowBack sx={{ mr: "8px" }} />
								Review Status
							</Button>
							<Box sx={{ flex: 1 }} />
							<Box>
								<Button
									size="small"
									aria-label="view files"
									onClick={openAttachments}
									startIcon={<AttachFileOutlined />}
									disabled={is_organization_viewer_only}
								>
									files
								</Button>
								<Box sx={{ position: "relative" }}>
									<Button
										size="small"
										aria-label="view threads"
										onClick={openThreads}
										disabled={!isUserAllowedToViewOrCreateMessages || is_organization_viewer_only}
										startIcon={
											<Box
												sx={{ position: "relative", display: "inline-flex" }}
											>
												<ChatOutlined />
												{unreadMessgCount !== 0 && (
													<Box
														sx={{
															position: "absolute",
															top: -8,
															right: 18,
															bottom: 23,
															bgcolor: "error.main",
															color: "white",
															borderRadius: "50%",
															width: 20,
															height: 20,
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															fontSize: "0.75rem",
															zIndex: 0,
															boxShadow:
																"0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)",
														}}
													>
														{unreadMessgCount}
													</Box>
												)}
											</Box>
										}
									>
										messages
									</Button>
								</Box>
								<Button
									size="small"
									aria-label="view sent notifications"
									onClick={openSentNotifications}
									startIcon={<CircleNotificationsOutlinedIcon />}
									disabled={is_organization_viewer_only}
								>
									notifications
								</Button>
							</Box>
						</Box>

						{addendums != null && Object.keys(addendums).length > 0 ? (
							<>
								<Box
									sx={{
										border: "1px solid #0000001F",
										p: "20px 16px",
										display: "wrap",
										flexDirection: "row",
										alignItems: "center",
										justifyContent: "space-between",
									}}
								>
									{activeLabel !== "initial" ? (
										<>
											<Button
												disabled={
													"is_user_authorized" in addendums["initial"] &&
													addendums["initial"]["is_user_authorized"]
														? false
														: true
												}
												variant="text"
												onClick={() => setActiveAddendum("initial", esrId)}
											>
												<span
													style={
														addendums["initial"]?.['hidden']
															? {
																color: "red",
																textDecoration: "line-through",
																fontWeight: "bold",
															}
															: {}
													}
												>
													#{esrId}
												</span>
											</Button>
										</>
									) : (
										<>
											<Button
												disabled={
													"is_user_authorized" in addendums["initial"] &&
													addendums["initial"]["is_user_authorized"]
														? false
														: true
												}
												variant="contained"
												onClick={() => setActiveAddendum("initial", esrId)}
											>
												<span
													style={
														addendums["initial"]?.['hidden']
															? {
																color: "red",
																textDecoration: "line-through",
																fontWeight: "bold",
															}
															: {}
													}
												>
													#{esrId}
												</span>
											</Button>
										</>
									)}

									{Object.keys(addendums)
										.filter((label) => label !== "initial")
										.sort()
										.map(function (keyName, keyIndex) {
											return (
												<a key={`${keyIndex}-${keyName}`}>
													{activeLabel === keyName ? (
														<>
															<Button
																disabled={
																	"is_user_authorized" in addendums[keyName] &&
																	addendums[keyName]["is_user_authorized"]
																		? false
																		: true
																}
																variant="contained"
																style={{ maxWidth: "20px" }}
																onClick={() => {
																	setActiveAddendum(
																		keyName,
																		addendums[keyName].id,
																	);
																}}
															>
																<span
																	style={
																		addendums["keyName"]?.["hidden"]
																			? {
																					color: "red",
																					textDecoration: "line-through",
																					fontWeight: "bold",
																				}
																			: {}
																	}
																>
																	{keyName}
																</span>
															</Button>
														</>
													) : (
														<>
															<Button
																disabled={
																	"is_user_authorized" in addendums[keyName] &&
																	addendums[keyName]["is_user_authorized"]
																		? false
																		: true
																}
																variant="text"
																style={{ maxWidth: "20px" }}
																onClick={() => {
																	setActiveAddendum(
																		keyName,
																		addendums[keyName].id,
																	);
																}}
															>
																{keyName}
															</Button>
														</>
													)}
												</a>
											);
										})}
								</Box>
							</>
						) : (
							<></>
						)}

						<Box sx={{ overflowY: "scroll" }}>
							{questions.groups.map((group, idx) => (
								<Box
									key={`${idx}-${group.name}`}
									sx={{
										border: "1px solid #0000001F",
										p: "20px 16px",
										background:
											idx === curGroupIdx
												? "white"
												: group.changed && activeId !== "" && esrId != activeId
													? "lightyellow"
													: "transparent",
										display: "flex",
										flexDirection: "column",
										alignItems: "stretch",
										justifyContent: "center",
										cursor: idx === curGroupIdx ? "default" : "pointer",
									}}
									onClick={() => {
										if (idx !== curGroupIdx) {
											navigate(
												`/esr/${esrId}/${activeLabel}/${activeId}/${idx}`,
											);
										}
									}}
								>
									<div
										style={{
											width: "300px",
											display: "flex",
											justifyContent: "space-between",
										}}
									>
										<Typography fontSize={16} fontWeight={600}>
											{group.name}
										</Typography>
										{!esr_submitted && group.complete ? (
											<>
												<DoneAllIcon
													style={{ color: "green" }}
													fontSize="small"
												/>
											</>
										) : (
											<></>
										)}
									</div>
									<Collapse in={idx === curGroupIdx}>
										<Box sx={{ ml: "20px" }}>
											{group.subgroups.map((subgroup, i) => {
												return (
													<div
														key={`${i}-${subgroup.name}`}
														style={{
															display: "flex",
															justifyContent: "space-between",
															backgroundColor:
																subgroup.changed &&
																activeId !== "" &&
																esrId != activeId
																	? "lightyellow"
																	: "transparent",
														}}
													>
														<Typography
															sx={{
																fontSize: 14,
																fontWeight: 400,
																cursor: "pointer",
																"&:hover": {
																	textDecoration: "underline",
																},
															}}
															onClick={() => {
																scrollToSubgroup(subgroup.name);
															}}
														>
															{subgroup.name}
														</Typography>
														{!esr_submitted && subgroup.complete ? (
															<>
																<DoneIcon
																	style={{ color: "green" }}
																	fontSize="small"
																/>
															</>
														) : (
															<></>
														)}
													</div>
												);
											})}
										</Box>
									</Collapse>
								</Box>
							))}

							{showSubmitBtn ? (
								<Box sx={{ mx: "12px", my: "16px" }}>
									<ConfirmButton
										disabled={!enableSubmitBtn}
										button={
											<Button
												disabled={!enableSubmitBtn}
												variant="contained"
												color="primary"
												size="large"
												fullWidth
											>
												<Add sx={{ mr: "8px" }} />
												{submitLabel}
											</Button>
										}
										title={submitLabel}
										details="Do you attest to the accuracy of the information entered?"
										confirmAction="Yes"
										cancelAction="No"
										onConfirm={submitForm}
										onCancel={() => {
											dispatch({
												type: "SET_ERROR",
												error: {
													message:
														"The ESR cannot be submitted until the accuracy of the information entered has been verified.",
												},
											});
										}}
									/>
								</Box>
							) : (
								<></>
							)}
							{/* {showRequestChangeResponse && (
								<>
									<Box sx={{ mx: "12px", my: "16px" }}>
										<ConfirmButton
											button={
												<Button
													variant="contained"
													color="warning"
													size="large"
													fullWidth
												>
													Request Response Change
												</Button>
											}
											title="Request Response Change"
											details={
												<>
													To request a change to an ESR response, an Error
													Ticket must be submitted.
													<br />
													<br />
													<Typography
														component="span"
														sx={{
															fontWeight: "bold",
															backgroundColor: "#fff59d",
															px: 1,
															py: 0.25,
															borderRadius: 1,
														}}
													>
														Choose Issue Type: QID_MODIFY_RESPONSE
													</Typography>
													<br />
													<br />
													Would you like to open the Error Reporting System?
												</>
											}
											confirmAction="Open Error Reporting System"
											cancelAction="Cancel"
											onConfirm={handleRequestResponseChange}
											onCancel={() => {}}
										/>
									</Box>
								</>
							)} */}
						</Box>
					</Stack>
					<Box sx={{ flex: "auto", overflow: "scroll" }}>
						<LocalizationProvider key="loc_provider" dateAdapter={AdapterDayjs}>
							<Box
								sx={{
									minWidth: 800,
									p: "80px 100px",
									position: "relative",
									zIndex: 0,
								}}
							>
								<Stack direction="row">
									<Typography fontSize={24} fontWeight={700}>
										Environmental Survey Request #{esrId}
										{activeId != esrId
											? activeLabel !== "initial"
												? activeLabel
												: ""
											: ""}
									</Typography>
									<Tooltip
										title={
											hasPendingResponses ? "Save or discard changes first" : ""
										}
									>

                                    <span>
                                      <Button
                                          sx={{ml: "10px"}}
                                          startIcon={
                                              isDownloading ? (
                                                  <CircularProgress size={18}/>
                                              ) : (
                                                  <FileDownload/>
                                              )
                                          }
                                          variant="outlined"
                                          disabled={hasPendingResponses || isDownloading}
                                          onClick={async () => {
                                              try {
                                                  setIsDownloading(true);
                                                  await download(
                                                      getBackendURL(`/esr/download_esr/${activeId}`)
                                                  );
                                              } finally {
                                                  setIsDownloading(false);
                                              }
                                          }}
                                      >
                                        {isDownloading ? "Generating PDF..." : "Download PDF"}
                                      </Button>
                                    </span>

									</Tooltip>
									{user?.admin && (
										<Tooltip
											title={
												bulkEditGrantee
													? `Bulk edit currently granted to ${bulkEditGrantee}`
													: "Grant a user permission to edit responses in this ESR"
											}
										>
											<span>
												<Button
													sx={{ ml: "10px" }}
													startIcon={<GroupAdd />}
													variant="outlined"
													color="secondary"
													disabled={hasPendingResponses}
													onClick={() => setBulkEditDialogOpen(true)}
												>
													Grant Bulk Edit
												</Button>
											</span>
										</Tooltip>
									)}
								</Stack>
								{activeId !== "" && esrId !== "" && activeId != esrId ? (
									<>
										<Typography bgcolor="lightyellow">
											Responses changed for this addendum are <b>highlighted</b>
											.
										</Typography>
									</>
								) : null}
								{curGroupIdx < questions.groups.length ? (
									<ESRQuestionGroup {...questions.groups[curGroupIdx]} />
								) : null}
								<Stack direction="row" sx={{ alignItems: "center" }}>
									<Box sx={{ flex: "auto" }} />
									{hasPendingResponses && (
										<>
											<Box
												sx={{
													position: "fixed",
													bottom: 16,
													right: 16,
													display: "flex",
													gap: 2,
													zIndex: 1200, // stays above content
													backgroundColor: "rgba(255, 255, 255, 0.9)", // optional: soft background
													padding: "8px 16px",
													borderRadius: "12px",
													boxShadow: 3,
												}}
											>
												<Button
													onClick={handleDiscardResponses}
													color="primary"
												>
													Continue Without Saving
												</Button>
												<Button
													onClick={handleSaveBulkResponses}
													variant="contained"
												>
													Save Changes
												</Button>
											</Box>
										</>
									)}
									{curGroupIdx !== questions.groups.length - 1 &&
										!hasPendingResponses && (
											<Button
												disabled={curGroupIdx === questions.groups.length - 1}
												variant="contained"
												onClick={() =>
													navigate(
														`/esr/${esrId}/${activeLabel}/${activeId}/${curGroupIdx + 1}`,
													)
												}
											>
												Continue
											</Button>
										)}

									{showSubmitBtn &&
										curGroupIdx === questions.groups.length - 1 && (
											<Box sx={{ mx: "12px", my: "16px" }}>
												<ConfirmButton
													disabled={!enableSubmitBtn}
													button={
														<Button
															disabled={!enableSubmitBtn}
															variant="contained"
															color="primary"
															size="large"
															fullWidth
														>
															<Add sx={{ mr: "8px" }} />
															{submitLabel}
														</Button>
													}
													title={submitLabel}
													details="Do you attest to the accuracy of the information entered?"
													confirmAction="Yes"
													cancelAction="No"
													onConfirm={submitForm}
													onCancel={() => {
														dispatch({
															type: "SET_ERROR",
															error: {
																message:
																	"The ESR cannot be submitted until the accuracy of the information entered has been verified.",
															},
														});
													}}
												/>
											</Box>
										)}
								</Stack>
							</Box>
						</LocalizationProvider>
					</Box>
				</Stack>
			)}
			{!(is_user_permitted_to_view_esr || user?.admin) && (
				<Box>
					<Alert variant="filled" severity="error">
						You are not permitted to view ESR# {esrId}.
					</Alert>
				</Box>
			)}
			<GrantEditPermissionDialog 
				open={bulkEditDialogOpen}
				title={`Grant Bulk Edit for ESR #${esrId}`}
				details="The user will be able to edit responses they are permitted to modify (user groups or Sponsor) or originally responded to."
				onClose={() => setBulkEditDialogOpen(false)}
				onGrant={(email) => {
					dispatch(
						updateQuestionResponse(
							esrId,
							activeId,
							BULK_EDIT_SPECIAL_UNLOCK_QID,
							email,
							undefined,
							"single",
							false,
						),
					);
				}}
			/>
			<Dialog open={moreChangesDialogOpen}>
				<DialogTitle>Changes Saved</DialogTitle>
				<DialogContent>
					<Typography>Do you have any more changes to make?</Typography>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setMoreChangesDialogOpen(false);
							dispatch(revokeSpecialEditPermissions(activeId, user?.email ?? ''));
						}}
					>
						No
					</Button>
					<Button variant="contained" onClick={() => setMoreChangesDialogOpen(false)}>
						Yes
					</Button>
				</DialogActions>
			</Dialog>
			{isUserAllowedToViewOrCreateMessages && <FloatingMessage />}
		</Box>
	) : (
		<Spinner />
	);
};


